import React from 'react'
import PageTitle from '../components/title/PageTitle'
import FaqSection from '../components/FaqSection'
import ElectionImage from '../assets/images/home-figma/election-pulse-preview.png'
import ElectionWhyImage from '../assets/images/election-pulse/why/public-sentiment-phone.png'
import HeroBackground from '../assets/images/election-pulse/hero-bg.png'
import HeroDeviceFrame from '../assets/images/election-pulse/desktop-and-phone-frame.png'
import PresidentialPollImg from '../assets/images/election-pulse/presidential-poll.png'
import LiveResultImg from '../assets/images/election-pulse/live-result-overall.png'
import LiveResultsImg from '../assets/images/election-pulse/live-results.png'
import ShareResultImg from '../assets/images/election-pulse/share-result.png'
import Insight01 from '../assets/images/election-pulse/insights-explored/01.png'
import Insight02 from '../assets/images/election-pulse/insights-explored/02.png'
import Insight03 from '../assets/images/election-pulse/insights-explored/03.png'
import Insight04 from '../assets/images/election-pulse/insights-explored/04.png'
import TransparencyImg from '../assets/images/election-pulse/transparency-image.png'
import JoinConvoImg from '../assets/images/election-pulse/join-convo-image.png'
import GradientBg from '../assets/images/election-pulse/gradient-bg.png'
import MegaphoneIcon from '../assets/svgs/election-pulse/megaphone.svg'
import UserGroupIcon from '../assets/svgs/election-pulse/user-group.svg'
import FileChartIcon from '../assets/svgs/election-pulse/file-chart.svg'
import VoterOne from '../assets/images/election-pulse/voter-1.png'
import VoterTwo from '../assets/images/election-pulse/voter-2.png'
import VoterThree from '../assets/images/election-pulse/voter-3.png'
import VoterFour from '../assets/images/election-pulse/voter-4.png'

const purpose = [
  { icon: MegaphoneIcon, title: 'Express Your Voice', text: 'Share your political preference in seconds.' },
  { icon: UserGroupIcon, title: 'Explore Public Sentiment', text: 'See what Nigerians across the country are thinking.' },
  { icon: FileChartIcon, title: 'Track Election Trends', text: 'Follow changes in voter sentiment as they happen.' },
]

const journeys = [
  { title: 'Vote in National Polls', text: 'Participate in presidential, governorship and House of Representatives polls.', action: 'Vote Now', type: 'ballot' },
  { title: 'View Live Results', text: 'See real-time voting trends across Nigeria.', action: 'Explore Results', type: 'results' },
  { title: 'Explore State Insights', text: 'See how preferences vary across Nigeria\'s states and constituencies.', action: 'View State Insights', type: 'states' },
  { title: 'Share Results', text: 'Share findings with friends and social networks.', action: 'Share Election Pulse', type: 'share' },
]

const journeyImgs = { ballot: PresidentialPollImg, results: LiveResultImg, states: LiveResultsImg, share: ShareResultImg }

const insightCards = [
  { title: 'Election Preferences', text: 'Which candidates are leading across different regions.', image: Insight01 },
  { title: 'State Level Trends', text: 'How voter sentiment differs across Nigeria.', image: Insight02 },
  { title: 'Confidence in Elections', text: 'How confident voters feel that their votes will count.', image: Insight03 },
  { title: 'Demographic Insights', tags: ['Age Groups', 'PVC Status', 'State of Residence', 'Participation Trends'], image: Insight04 },
]

const faqContent = {
  title: 'Frequently Asked Questions',
  columns: [
    [
      { title: 'Do I need to create an account?', desc: 'No. You can participate in Election Pulse without creating an account.' },
      { title: 'Can I see results without voting?', desc: 'Yes. Live public sentiment results are available to explore at any time.' },
      { title: 'Are these official election results?', desc: 'No. Election Pulse reflects public sentiment and should not be interpreted as official election results.' },
    ],
    [
      { title: 'Is my information public?', desc: 'No personally identifying information is shown in public results.' },
      { title: 'Can I vote more than once?', desc: 'Safeguards help keep participation fair and prevent duplicate responses.' },
    ],
  ],
}

const checkIcon = (
  <svg className="check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="7" fill="#4CAF50" />
    <path d="M4 7L6.5 9.5L10.5 5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const ElectionPulse = () => {
  const renderVoterFaces = () =>
    [VoterOne, VoterTwo, VoterThree, VoterFour].map((voter, index) => (
      <img src={voter} alt="" key={index} />
    ))

  const renderPurposeCards = () =>
    purpose.map((item) => (
      <article key={item.title}>
        <span><img src={item.icon} alt="" /></span>
        <h3>{item.title}</h3>
        <p>{item.text}</p>
      </article>
    ))

  const renderJourneyCards = () =>
    journeys.map((item, index) => (
      <article
        className={`election-journey-row ${index % 2 ? 'reverse' : ''}`}
        id={item.type === 'results' ? 'results' : undefined}
        key={item.title}
      >
        <div>
          <h2>{item.title}</h2>
          <p>{item.text}</p>
          <a href="https://public.clientshot.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary">{item.action}</a>
        </div>
        <div className="pulse-phone-stage">
          <img
            src={journeyImgs[item.type]}
            alt={
              item.title === 'Vote in National Polls'
                ? 'Presidential poll voting interface'
                : item.title === 'View Live Results'
                  ? 'Live election results overview'
                  : item.title === 'Explore State Insights'
                    ? 'State-level election results display'
                    : 'Share election results interface'
            }
            className="pulse-journey-img"
          />
        </div>
      </article>
    ))

  const renderInsightCards = () =>
    insightCards.map((card) => (
      <article key={card.title}>
        <div className="insight-photo">
          <img
            src={card.image}
            alt={
              card.title === 'Election Preferences'
                ? 'Map showing candidate preferences across Nigerian regions'
                : card.title === 'State Level Trends'
                  ? 'Chart displaying voter sentiment trends by state'
                  : card.title === 'Confidence in Elections'
                    ? 'Graph showing voter confidence levels'
                    : 'Infographic of demographic insights including age groups and participation trends'
            }
          />
        </div>
        <div className="insight-content">
          <h3>{card.title}</h3>
          {card.tags ? (
            <div className="insight-tags">
              {card.tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          ) : (
            <p>{card.text}</p>
          )}
        </div>
      </article>
    ))

  return (
    <main className="election-page">
    <PageTitle title="Election Pulse | Clientshot" />

    <section className="election-hero">
      <img src={HeroBackground} className="election-hero-bg" alt="" />
      <div className="election-hero-shade"></div>
      <div className="election-hero-content">
        <div className="election-live-pill">
          <span className="election-voter-faces" aria-hidden="true">
            {renderVoterFaces()}
          </span>
          <span>2,431 Nigerians voting right now</span>
        </div>
        <h1>Discover What Nigerians Are Thinking Ahead of the Elections</h1>
        <p>A nationwide public polling platform designed to capture political preferences, voter sentiment, and election trends across Nigeria.</p>
        <div className="election-actions"><a href="https://public.clientshot.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary">Vote Now</a><a href="https://public.clientshot.com/" target="_blank" rel="noopener noreferrer" className="btn btn-alt">View Results</a></div>
      </div>
      <div className="election-hero-device" aria-label="Election Pulse voting and results screens">
        <img src={HeroDeviceFrame} loading="lazy" alt="" />
      </div>
    </section>

    <section className="election-shell election-why">
      <h2>Why <span>Election Pulse</span> Exists</h2>
      <p>Election seasons create some of the largest public conversations in Nigeria. Every day, millions of Nigerians discuss candidates, parties, policies, and the future of the country across social media, communities, and workplaces.</p>
      <div className="election-photo"><img src={ElectionWhyImage} alt="A voter using Election Pulse on a phone" /></div>
      <div className="election-purpose-grid">{renderPurposeCards()}</div>
    </section>

    <section className="election-journey" id="vote">
      <div className="election-mini-label">✦ What You Can Do</div>
      <div className="election-shell">{renderJourneyCards()}</div>
    </section>

    <section className="election-insights">
      <h2>What Insights Can Be Explored?</h2>
      <div className="election-insight-grid">{renderInsightCards()}</div>
    </section>

    <section className="election-transparency">
      <div className="election-transparency-row">
        <div className="transparency-image"><img src={TransparencyImg} alt="Election Pulse transparency and integrity measures overview" /></div>
        <div className="transparency-content">
          <div className="transparency-text">
            <h2>Designed for Transparency</h2>
            <p>We are committed to maintaining credible and trustworthy polling results.</p>
            <p>Election Pulse uses multiple integrity checks to help reduce abuse and duplicate voting while protecting participant privacy.</p>
          </div>
          <div className="transparency-integrity">
            <h4>Integrity Measures</h4>
            <div className="transparency-tags">
              <span>{checkIcon}Duplicate Vote Protection</span>
              <span>{checkIcon}Device Verification</span>
              <span>{checkIcon}Suspicious activity detection</span>
              <span>{checkIcon}Network monitoring</span>
            </div>
          </div>
          <a href="https://public.clientshot.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary election-transparency-btn">Vote Now</a>
        </div>
      </div>
      <p className="election-disclaimer">This platform is intended to reflect public sentiment and should not be interpreted as an official election result.</p>
    </section>

    <section className="election-final">
      <div className="election-final-panel election-final-panel-bg" style={{backgroundImage:`url(${GradientBg})`}}>
        <div className="election-final-inner">
          <div className="election-final-photo"><img src={JoinConvoImg} alt="Nigerians engaging in the national election conversation" /><div className="election-final-photo-overlay"></div></div>
          <div className="election-final-text"><h2>Join the National Conversation</h2><p>Discover how Nigerians are thinking, voting and engaging ahead of the elections.</p><div className="election-actions"><a href="https://public.clientshot.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary">Vote Now</a><a href="https://public.clientshot.com/" target="_blank" rel="noopener noreferrer" className="btn btn-alt">View Live Results</a></div></div>
        </div>
      </div>
    </section>

    <div className="election-faq"><FaqSection content={faqContent} /></div>
    </main>
  )
}

export default ElectionPulse
